import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { parseFastApiValidationMessage } from '../../helpers/api-error.helper';
import { createEmployeeTrainingSession } from '../../services/employee-training.service';
import type { CreateEmployeeTrainingBody, EmployeeTrainingData } from '../../types/employee-training.type';
import PageTitleIcon from '../PageTitleIcon';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

const TITLE_MAX = 200;
const NOTES_MAX = 1000;
const REQ_CODE_MAX = 100;
const CATEGORY_MAX = 100;
const URL_MAX = 2000;

/** Preset values sent as `data.training_category` (lowercase / snake_case for API consistency). */
const TRAINING_CATEGORY_PRESET_VALUES = [
  'safety',
  'environmental',
  'health',
  'quality_compliance',
  'operations',
  'technical',
  'leadership',
  'governance',
  'waste_recycling',
  'esg_reporting',
] as const;

const CATEGORY_OTHER = '__other__';

function isValidOptionalUrl(s: string): boolean {
  const trimmed = s.trim();
  if (trimmed === '') return true;
  if (trimmed.length > URL_MAX) return false;
  try {
    const u = new URL(trimmed);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export type RecordTrainingHelpTip = {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onNext: () => void;
  nextLabel: string;
};

export type RecordTrainingDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  userId: string;
  /** Shown in dialog title */
  employeeLabel?: string;
  /** Optional in-dialog help coachmarks (Knowledge Base "Record training" flow). */
  formHelpTip?: RecordTrainingHelpTip;
  saveHelpTip?: RecordTrainingHelpTip;
};

const RecordTrainingDialog: React.FC<RecordTrainingDialogProps> = ({
  open,
  onClose,
  organizationId,
  userId,
  employeeLabel,
  formHelpTip,
  saveHelpTip,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState('');
  const [sessionDate, setSessionDate] = useState<Date | null>(new Date());
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [requirementCode, setRequirementCode] = useState('');
  const [categorySelect, setCategorySelect] = useState('');
  const [categoryOther, setCategoryOther] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const resetForm = useCallback(() => {
    setHours('');
    setSessionDate(new Date());
    setTitle('');
    setNotes('');
    setRequirementCode('');
    setCategorySelect('');
    setCategoryOther('');
    setExternalUrl('');
    setFormError(null);
  }, []);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const mutation = useMutation({
    mutationFn: (body: CreateEmployeeTrainingBody) =>
      createEmployeeTrainingSession(organizationId, userId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['training-summary', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['training-sessions', organizationId, userId] });
      resetForm();
      onClose();
    },
    onError: (error: unknown) => {
      const ax = error as AxiosError<{ detail?: unknown }>;
      const status = ax.response?.status;
      const detail = ax.response?.data?.detail;
      if (status === 422) {
        setFormError(parseFastApiValidationMessage(detail));
        return;
      }
      if (status === 403 || status === 404) {
        const msg =
          typeof detail === 'string'
            ? detail
            : parseFastApiValidationMessage(detail) || ax.message || 'Request failed';
        setSnackbar({ open: true, message: msg });
        return;
      }
      setSnackbar({
        open: true,
        message: (detail as string) || ax.message || t('employeeTraining.recordDialog.saveFailed'),
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const hoursNum = Number(String(hours).replace(',', '.'));
    if (!Number.isFinite(hoursNum) || hoursNum <= 0) {
      setFormError(t('employeeTraining.recordDialog.hoursInvalid'));
      return;
    }
    if (!sessionDate) {
      setFormError(t('employeeTraining.recordDialog.trainingDateRequired'));
      return;
    }
    if (title.length > TITLE_MAX) {
      setFormError(t('employeeTraining.recordDialog.titleMax', { max: TITLE_MAX }));
      return;
    }
    if (notes.length > NOTES_MAX) {
      setFormError(t('employeeTraining.recordDialog.notesMax', { max: NOTES_MAX }));
      return;
    }
    let resolvedCategory = '';
    if (categorySelect === CATEGORY_OTHER) {
      resolvedCategory = categoryOther.trim();
      if (!resolvedCategory) {
        setFormError(t('employeeTraining.recordDialog.categoryRequired'));
        return;
      }
    } else {
      resolvedCategory = categorySelect.trim();
    }
    if (requirementCode.length > REQ_CODE_MAX || resolvedCategory.length > CATEGORY_MAX) {
      setFormError(t('employeeTraining.recordDialog.codeOrCategoryMax'));
      return;
    }
    if (!isValidOptionalUrl(externalUrl)) {
      setFormError(t('employeeTraining.recordDialog.urlInvalid'));
      return;
    }

    const session_date = format(sessionDate, 'yyyy-MM-dd');
    const data: EmployeeTrainingData = {};
    if (requirementCode.trim()) data.requirement_code = requirementCode.trim();
    if (resolvedCategory) data.training_category = resolvedCategory;
    if (externalUrl.trim()) data.external_reference_url = externalUrl.trim();

    const body: CreateEmployeeTrainingBody = {
      hours: hoursNum,
      session_date,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(Object.keys(data).length > 0 ? { data } : {}),
    };

    mutation.mutate(body);
  };

  const handleDialogClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <PageTitleIcon icon={<PostAddOutlinedIcon />} size={40} />
          <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
            <Typography
              component="h2"
              variant="h6"
              sx={{ fontWeight: 600, lineHeight: 1.3 }}
            >
              {t('employeeTraining.recordDialog.title')}
            </Typography>
            {employeeLabel ? (
              <Typography
                component="p"
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.35,
                  mb: 0,
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={employeeLabel}
              >
                {employeeLabel}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            aria-label={t('common.close')}
            onClick={handleDialogClose}
            disabled={mutation.isPending}
            size="small"
            sx={{ color: 'text.secondary', mt: -0.25, ml: 0.5 }}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <SetupCoachmark
              open={Boolean(formHelpTip?.open)}
              title={formHelpTip?.title ?? ''}
              subtitle={formHelpTip?.subtitle ?? ''}
              onDismiss={formHelpTip?.onDismiss ?? (() => undefined)}
              onNext={formHelpTip?.onNext}
              nextLabel={formHelpTip?.nextLabel}
              fullWidth
              pageDim={false}
            >
              <Box id="help-training-record-form">
                {formError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formError}
                  </Alert>
                ) : null}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                    <TextField
                      label={t('employeeTraining.recordDialog.hours')}
                      type="text"
                      inputMode="decimal"
                      value={hours}
                      onChange={(ev) => setHours(ev.target.value)}
                      required
                      fullWidth
                      helperText={t('employeeTraining.recordDialog.hoursHelper')}
                    />
                    <DatePicker
                      label={t('employeeTraining.recordDialog.trainingDate')}
                      value={sessionDate}
                      onChange={(d) => setSessionDate(d)}
                      slotProps={{ textField: { fullWidth: true, required: true } }}
                    />
                    <TextField
                      label={t('employeeTraining.recordDialog.titleOptional')}
                      value={title}
                      onChange={(ev) => setTitle(ev.target.value)}
                      inputProps={{ maxLength: TITLE_MAX }}
                      fullWidth
                    />
                    <TextField
                      label={t('employeeTraining.recordDialog.notesOptional')}
                      value={notes}
                      onChange={(ev) => setNotes(ev.target.value)}
                      inputProps={{ maxLength: NOTES_MAX }}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label={t('employeeTraining.recordDialog.code')}
                      value={requirementCode}
                      onChange={(ev) => setRequirementCode(ev.target.value)}
                      inputProps={{ maxLength: REQ_CODE_MAX }}
                      fullWidth
                      helperText={t('employeeTraining.recordDialog.codeHelper')}
                    />
                    <FormControl fullWidth>
                      <InputLabel id="training-category-label">
                        {t('employeeTraining.recordDialog.category')}
                      </InputLabel>
                      <Select
                        labelId="training-category-label"
                        id="training-category"
                        label={t('employeeTraining.recordDialog.category')}
                        value={categorySelect}
                        onChange={(e: SelectChangeEvent<string>) => {
                          setCategorySelect(e.target.value);
                          if (e.target.value !== CATEGORY_OTHER) setCategoryOther('');
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('common.none')}</em>
                        </MenuItem>
                        {TRAINING_CATEGORY_PRESET_VALUES.map((value) => (
                          <MenuItem key={value} value={value}>
                            {t(`employeeTraining.categories.${value}`)}
                          </MenuItem>
                        ))}
                        <MenuItem value={CATEGORY_OTHER}>
                          {t('employeeTraining.recordDialog.categoryOther')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    {categorySelect === CATEGORY_OTHER ? (
                      <TextField
                        label={t('employeeTraining.recordDialog.specifyCategory')}
                        value={categoryOther}
                        onChange={(ev) => setCategoryOther(ev.target.value)}
                        inputProps={{ maxLength: CATEGORY_MAX }}
                        fullWidth
                        required
                        helperText={t('employeeTraining.recordDialog.specifyCategoryHelper')}
                      />
                    ) : null}
                    <TextField
                      label={t('employeeTraining.recordDialog.referenceUrl')}
                      value={externalUrl}
                      onChange={(ev) => setExternalUrl(ev.target.value)}
                      inputProps={{ maxLength: URL_MAX }}
                      fullWidth
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </SetupCoachmark>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleDialogClose} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <SetupCoachmark
              open={Boolean(saveHelpTip?.open)}
              title={saveHelpTip?.title ?? ''}
              subtitle={saveHelpTip?.subtitle ?? ''}
              onDismiss={saveHelpTip?.onDismiss ?? (() => undefined)}
              onNext={saveHelpTip?.onNext}
              nextLabel={saveHelpTip?.nextLabel}
              pageDim={false}
            >
              <Button
                id="help-training-record-save"
                type="submit"
                variant="contained"
                disabled={mutation.isPending}
              >
                {t('common.save')}
              </Button>
            </SetupCoachmark>
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RecordTrainingDialog;
